'use client';

import { Button } from '@/components/Button/Button';
import { useState, useCallback } from 'react';
import { canShowCustomerLogin } from '@/lib/role-permissions/permissions-engine';
import { useSession } from 'next-auth/react';
import { LoginModal } from '@/components/Modal/LoginModal/LoginModal';
import { customerSignOut, getLogoutRedirectRoute } from '@/components/Modal/LoginModal/utils';
import { useCustomerAuthStore } from '@/stores/customer-auth-store';
import { useRouter, useParams, usePathname } from 'next/navigation';
import { ActionWarningModal } from '@/components/[Shared]/ActionWarningModal/ActionWarningModal';
import { useTranslations } from 'next-intl';
import { useNotifications } from '@/providers/notification-provider';
import { analyticsEventHandlers } from '@/lib/analytics/events/generalAnalyticsEvents';

type CustomerAuthButtonsProps = { countryCode: string; variant: 'navbar' | 'secondary' };

export function CustomerAuthButtons({ countryCode, variant }: CustomerAuthButtonsProps) {
	const [loginModalActive, setLoginModalActive] = useState<boolean>(false);
	const { data, status } = useSession();
	const isCustomerAuthenticated = useCustomerAuthStore((s) => s.isAuthenticated);
	const router = useRouter();
	const { locale, retailer }: { locale: string; retailer?: string } = useParams();
	const pathname = usePathname();
	const logoutRedirectRoute = getLogoutRedirectRoute(pathname, locale, retailer);
	const t = useTranslations('global.navbar');
	const tLogout = useTranslations('global.logout-modal');
	const { triggerNotificationMessage } = useNotifications();

	function handleOpenLoginModal() {
		if (canShowCustomerLogin({ customerAuthenticated: isCustomerAuthenticated, colleagueAuthenticated: Boolean(data) })) {
			setLoginModalActive(true);
		}

		analyticsEventHandlers.trackLoginButtonClicked();
	}

	const handleLoginSuccess = useCallback(() => {
		setLoginModalActive(false);
	}, []);

	const handleLogout = useCallback(() => {
		customerSignOut(() => {
			if (logoutRedirectRoute.route) {
				router.push(logoutRedirectRoute.route);
			}
		});
		triggerNotificationMessage(tLogout('title'), tLogout('message'), tLogout('dismiss_button'));

		// TODO: Handle unhappy paths, i.e. if the Customer Portal is down
	}, [logoutRedirectRoute.route, router, triggerNotificationMessage, tLogout]);

	if (status === 'loading') {
		// Return null to avoid rendering the component with incorrect auth state and hydration errors
		return null;
	}

	if (isCustomerAuthenticated && logoutRedirectRoute.shouldPromptWarning) {
		return (
			<ActionWarningModal
				title={t('log-out-warning-modal.title')}
				onConfirm={handleLogout}
				confirmButtonLabel={t('log-out-warning-modal.log_out_cta_label')}
				cancelButtonLabel={t('log-out-warning-modal.cancel_cta_label')}
				description={t('log-out-warning-modal.description')}
				modalTriggerAsChild
				modalTriggerChild={
					<Button variant={variant} icon="log-out" iconPosition="left" iconSize="1em" data-testid="btn_logout">
						{t('log_out_button_label')}
					</Button>
				}
			/>
		);
	}

	if (isCustomerAuthenticated && !logoutRedirectRoute.shouldPromptWarning) {
		return (
			<Button variant={variant} icon="log-out" iconPosition="left" iconSize="1em" onClick={handleLogout} data-testid="btn_logout">
				{t('log_out_button_label')}
			</Button>
		);
	}

	return (
		<>
			{/* TODO: This comp loads even if its not triggered to load, its a perf issue. [LoginModalPerf] */}
			<LoginModal
				hideModalTrigger
				onLoginSuccess={handleLoginSuccess}
				onOpenChange={setLoginModalActive}
				open={Boolean(loginModalActive)}
				countryCode={countryCode}
			/>
			<Button variant={variant} icon="person" iconPosition="left" iconSize="1em" onClick={handleOpenLoginModal} data-testid="btn_login">
				{t('log_in_button_label')}
			</Button>
		</>
	);
}
